// src/contexts/NotificationContext.tsx - COMPLETE FIXED VERSION

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

export interface Notification {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  category: 'system' | 'device' | 'maintenance' | 'safety' | 'fuel' | 'geofence';
  deviceId?: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  metadata?: any;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  hasMore: boolean;
  loading: boolean;
  loadMore: () => Promise<void>;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  socket: Socket | null;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

// Helper function to play notification sound
const playNotificationSound = () => {
  try {
    const audio = new Audio('/mixkit-message-pop-alert-2354.mp3');
    audio.volume = 0.5;
    audio.play().catch(err => console.log('Could not play sound:', err));
  } catch (error) {
    console.log('Audio not supported');
  }
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  console.log('🚀 NotificationProvider MOUNTING');
  
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const unreadCount = notifications.filter(n => !n.read).length;

  // Get API URL based on environment
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:6162/api';
  const WS_URL = process.env.REACT_APP_WS_URL || 'http://localhost:6162';

  console.log('🌐 Notification Config:', { API_URL, WS_URL });

  // Debounce timer for refetch
  const refetchTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Fetch notifications function
  const fetchNotifications = useCallback(async (skipCount: number = 0, append: boolean = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('⚠️ No token found');
        return;
      }

      setLoading(true);
      const LIMIT = 20;

      console.log(`📡 Fetching notifications: ${API_URL}/notifications?limit=${LIMIT}&skip=${skipCount}`);

      const response = await fetch(
        `${API_URL}/notifications?limit=${LIMIT}&skip=${skipCount}&unreadOnly=false&includeArchived=false`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log(`Response status: ${response.status}`);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Notifications fetched:', data);

        if (data.success && data.data) {
          const fetchedNotifications = data.data.map((n: any) => ({
            id: n._id,
            type: n.type,
            category: n.category,
            title: n.title,
            message: n.message,
            timestamp: new Date(n.createdAt || n.timestamp),
            read: n.read,
            priority: n.priority,
            deviceId: n.deviceId,
            metadata: n.metadata || {}
          }));

          console.log(`✅ Processed ${fetchedNotifications.length} notifications`);

          setNotifications(prev => {
            const newList = append ? [...prev, ...fetchedNotifications] : fetchedNotifications;
            console.log(`📊 Total in state: ${newList.length}`);
            return newList;
          });

          setHasMore(fetchedNotifications.length === LIMIT);
        }
      } else {
        console.error(`❌ Failed to fetch: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // ✅ FIXED: Initial fetch on mount - runs once only
  useEffect(() => {
    console.log('🔄 Initial notification fetch on mount');
    
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('⚠️ No token, skipping initial fetch');
      return;
    }
    
    // Call fetch immediately on mount
    fetchNotifications(0, false);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ Empty dependency array - only runs once on mount

  // Debounced refetch function to prevent multiple rapid API calls
  const debouncedRefetch = useCallback(() => {
    // Clear any existing timer
    if (refetchTimerRef.current) {
      clearTimeout(refetchTimerRef.current);
    }

    // Set new timer
    refetchTimerRef.current = setTimeout(() => {
      console.log('🔄 Debounced refetch executing...');
      fetchNotifications(0, false);
      refetchTimerRef.current = null;
    }, 500);
  }, [fetchNotifications]);

  // Socket.IO connection
  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      console.warn('No auth token found, skipping socket connection');
      return;
    }

    console.log('🔌 Connecting to WebSocket:', WS_URL);

    const newSocket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    newSocket.on('connect', () => {
      console.log('✅ WebSocket connected!');

      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const companyId = user.companyId?._id || user.companyId;

          if (companyId) {
            console.log(`🏢 Joining company room: company_${companyId}`);
            newSocket.emit('join_company_room', companyId);
          }
        } catch (error) {
          console.error('❌ Error joining company room:', error);
        }
      }
    });

    newSocket.on('room_joined', (data: any) => {
      console.log('✅ Successfully joined room:', data.room);
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error.message);
    });

    // ✅ FIX: Refetch from database instead of adding to state
    newSocket.on('notification', (data: any) => {
      console.log('📬 Generic notification received:', data);
      
      // Debounced refetch to prevent multiple rapid API calls
      debouncedRefetch();

      // Play sound for high priority notifications
      if (data.priority === 'critical' || data.priority === 'high') {
        playNotificationSound();
      }
    });

    // ✅ FIX: Refetch from database instead of adding to state
    newSocket.on('geofence_notification', (data: any) => {
      console.log('🛡️ Geofence notification received:', data);

      // Debounced refetch to prevent multiple rapid API calls
      debouncedRefetch();

      // Play sound for critical geofence events
      if (data.priority === 'critical') {
        playNotificationSound();
      }
    });

    // ✅ FIX: Refetch from database instead of adding to state
    newSocket.on('device_alert', (data: any) => {
      console.log('📡 Device alert received:', data);
      
      // Debounced refetch to prevent multiple rapid API calls
      debouncedRefetch();

      // Device alerts are typically high priority
      playNotificationSound();
    });

    setSocket(newSocket);

    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('📢 Browser notification permission:', permission);
      });
    }

    return () => {
      console.log('🔌 Closing WebSocket connection');
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
      }
      newSocket.close();
    };
  }, [WS_URL, debouncedRefetch]);

  // Load more function
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) {
      console.log('⏭️ Skip load more:', { hasMore, loading });
      return;
    }

    const currentCount = notifications.length;
    console.log('📄 Loading more notifications from skip:', currentCount);
    await fetchNotifications(currentCount, true);
  }, [notifications.length, hasMore, loading, fetchNotifications]);

  // Add notification to state (only used for manual/local notifications, not Socket.IO)
  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      read: false,
    };

    console.log('➕ Adding notification to state:', newNotification);

    setNotifications(prev => {
      const updated = [newNotification, ...prev];
      console.log('📊 Total notifications now:', updated.length);
      console.log('📊 Unread count:', updated.filter(n => !n.read).length);
      return updated;
    });

    // Browser notification
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        new Notification(newNotification.title, {
          body: newNotification.message,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: newNotification.category,
          requireInteraction: newNotification.priority === 'critical'
        });
      } catch (error) {
        console.log('Could not show browser notification:', error);
      }
    }

    // Play sound for high priority
    if (notification.priority === 'critical' || notification.priority === 'high') {
      playNotificationSound();
    }
  };

  // Mark as read
  const markAsRead = async (id: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );

    try {
      const token = localStorage.getItem('token');

      await fetch(
        `${API_URL}/notifications/${id}/read`,
        {
          method: 'PUT',
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('✅ Notification marked as read:', id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );

    try {
      const token = localStorage.getItem('token');

      await fetch(
        `${API_URL}/notifications/mark-all-read`,
        {
          method: 'PUT',
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('✅ All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Remove notification
  const removeNotification = async (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));

    try {
      const token = localStorage.getItem('token');

      await fetch(
        `${API_URL}/notifications/${id}`,
        {
          method: 'DELETE',
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('✅ Notification removed:', id);
    } catch (error) {
      console.error('Failed to remove notification:', error);
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    const notificationIds = notifications.map(n => n.id);
    setNotifications([]);

    try {
      const token = localStorage.getItem('token');

      await fetch(
        `${API_URL}/notifications/bulk-action`,
        {
          method: 'POST',
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            notificationIds,
            action: 'archive'
          })
        }
      );
      console.log('✅ All notifications cleared');
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
    }
  };

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    hasMore,
    loading,
    loadMore,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
    socket,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};